import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Sun, FileText, Loader2, PenLine, Mail, Phone } from "lucide-react";
import { brandDocument } from "@/lib/publicHost";
import { DocRow, DownloadAllButton, onColor } from "@/components/portal/PortalDocs";
import { NdaGate, ViewerGate, useViewerParam } from "@/components/portal/NdaGate";

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

// Expired, revoked or unknown client link: a Power Ray page that says who to ask. Never a GridForge error page.
function PortalUnavailable({ code }) {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/public/branding").then((r) => { setB(r.data); brandDocument(r.data.company_name, r.data.logo); }).catch(() => setB({})); }, []);
  const company = b?.company_name || "Power Ray LLC";
  const c = b?.contact || {};
  const primary = b?.primary || "#003E87";
  const headline = code === "not_found" ? "This page is not available." : "This link has expired. Ask Power Ray for a new one.";
  return (
    <div className="min-h-screen bg-slate-100 grid place-items-center px-4" data-testid="portal-unavailable">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-8 py-5 flex items-center gap-3" style={{ background: primary, color: onColor(primary) }}>
          {b?.logo ? <img src={b.logo} alt="" className="h-8 w-auto" /> : <Sun className="w-7 h-7" style={{ color: b?.accent || "#F4C400" }} />}
          <div className="font-bold">{company}</div>
        </div>
        <div className="px-8 py-7">
          <h1 className="text-lg font-semibold text-slate-900" data-testid="portal-unavailable-headline">{headline}</h1>
          <p className="text-sm text-slate-600 mt-2">For your documents, please contact {c.name || "Taylor"}{c.title ? `, ${c.title}` : ""} at {company}.</p>
          <div className="mt-4 space-y-1.5 text-sm">
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 underline" style={{ color: primary }}><Mail className="w-4 h-4" /> {c.email}</a>}
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 underline" style={{ color: primary }}><Phone className="w-4 h-4" /> {c.phone}</a>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PortfolioCard({ p, token, primary, preview }) {
  const docs = (p.documents || []).filter((d) => !d.superseded);
  const old = (p.documents || []).filter((d) => d.superseded);
  return (
    <section className="rounded-xl border border-slate-200 p-5" data-testid={`portal-portfolio-${p.portfolio_id}`}>
      <div>
        <h2 className="text-base font-semibold text-slate-900" data-testid="portal-portfolio-program">{p.program}</h2>
        <div className="text-xs text-slate-500 mt-0.5">{["Portfolio", p.round_label, p.scenario, p.sent_at ? `sent ${fmtDay(p.sent_at)}` : null].filter(Boolean).join(" · ")}</div>
        {p.sites?.length > 0 && <div className="text-xs text-slate-500 mt-1" data-testid="portal-portfolio-sites">Sites: {p.sites.map((s) => s.name).join(", ")}</div>}
      </div>
      <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
        {docs.map((d) => <DocRow key={d.item_id} token={token} d={d} primary={primary} preview={preview} />)}
      </ul>
      {old.length > 0 && (
        <details className="mt-2 text-xs text-slate-500" data-testid="portal-portfolio-superseded">
          <summary className="cursor-pointer">Earlier rounds ({old.length}) · superseded</summary>
          <ul className="mt-1 space-y-1">{old.map((d) => <li key={d.item_id} className="line-through text-slate-400">{d.label} · {d.round_label || "earlier round"}</li>)}</ul>
        </details>
      )}
    </section>
  );
}

function BidCard({ b, token, primary, preview, viewer }) {
  const docs = (b.documents || []).filter((d) => !d.superseded);
  const old = (b.documents || []).filter((d) => d.superseded);
  return (
    <section className="rounded-xl border border-slate-200 p-5" data-testid={`portal-bid-${b.bid_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-slate-900" data-testid="portal-bid-project">{b.project}</h2>
          <div className="text-xs text-slate-500 mt-0.5" data-testid="portal-bid-send-state">{[b.scope, b.round_label, b.send_state || (b.sent_at ? `Sent ${fmtDay(b.sent_at)}` : null)].filter(Boolean).join(" · ")}</div>
        </div>
        {b.proposal_page && (
          <a href={b.proposal_page} className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md px-3 py-1.5" style={{ background: primary, color: onColor(primary) }} data-testid="portal-esign-link">
            <PenLine className="w-4 h-4" /> Review &amp; e-sign the proposal
          </a>
        )}
      </div>
      <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
        {docs.map((d) => <DocRow key={d.item_id} token={token} d={d} primary={primary} preview={preview} viewer={viewer} />)}
        {docs.length === 0 && <li className="py-2 text-sm text-slate-400" data-testid="portal-bid-empty">Nothing new yet.</li>}
      </ul>
      {old.length > 0 && (
        <details className="mt-2 text-xs text-slate-500" data-testid="portal-superseded">
          <summary className="cursor-pointer">Earlier rounds ({old.length}) · superseded</summary>
          <ul className="mt-1 space-y-1">
            {old.map((d) => (
              <li key={d.item_id} className="line-through text-slate-400">{d.label} · {d.round_label || "earlier round"}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// One link per client contact: every bid released to them, and under each only what was sent. No login.
export default function ClientPortal() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const [d, setD] = useState(null);
  const [viewer, setViewer] = useViewerParam();
  const [status, setStatus] = useState("loading");
  const [code, setCode] = useState("not_found");

  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    html.classList.remove("dark");
    return () => { if (wasDark) html.classList.add("dark"); };
  }, []);
  useEffect(() => {
    const q = new URLSearchParams(); if (preview) q.set("preview", "1"); if (viewer) q.set("viewer", viewer);
    api.get(`/public/portal/${token}${q.toString() ? "?" + q.toString() : ""}`)
      .then((r) => { setD(r.data); setStatus("ok"); brandDocument(r.data.branding?.company_name, r.data.branding?.logo); })
      .catch((e) => { setCode(e.response?.data?.detail?.code || "not_found"); setStatus("error"); });
  }, [token, preview, viewer]);

  if (status === "loading") return <div className="min-h-screen grid place-items-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (status === "error" || !d) return <PortalUnavailable code={code} />;
  const b = d.branding || {};
  const primary = b.primary || "#003E87";
  const accent = b.accent || "#F4C400";
  const company = b.company_name || "Power Ray LLC";
  const pr = d.contact?.power_ray || b.contact || {};

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="client-portal">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-8 py-6 flex items-center justify-between gap-4" style={{ background: primary, color: onColor(primary) }}>
          <div className="flex items-center gap-3 min-w-0">
            {b.logo ? <img src={b.logo} alt="" className="h-9 w-auto shrink-0" /> : <Sun className="w-8 h-8 shrink-0" style={{ color: accent }} />}
            <div className="min-w-0"><div className="font-bold text-lg leading-tight truncate" data-testid="portal-company">{company}</div>
              <div className="text-xs opacity-80">Your proposals and documents</div></div>
          </div>
          <div className="text-right text-xs opacity-90 shrink-0"><div>Prepared for</div><div className="font-semibold text-sm" data-testid="portal-contact">{d.contact?.name || d.client_name || "—"}</div>{d.client_name && d.contact?.name ? <div className="opacity-80">{d.client_name}</div> : null}</div>
        </div>
        <div className="px-8 py-7 space-y-4">
          {d.gate?.state === "sign" ? <NdaGate token={token} gate={d.gate} primary={primary} company={company} onDone={(v) => setViewer(v)} />
          : d.gate?.state === "closed" ? <div className="rounded-xl border border-slate-200 p-6 text-sm text-slate-700" data-testid="nda-closed">{d.gate.message}</div>
          : d.gate?.state === "viewer" ? <ViewerGate token={token} gate={d.gate} primary={primary} company={company} onDone={(v) => setViewer(v)} />
          : (<>
          {preview && <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="portal-preview-banner">Viewing as the client · Power Ray test — nothing here is logged as the client.</div>}
          <div className="flex justify-end"><DownloadAllButton token={token} primary={primary} preview={preview} viewer={viewer} count={(d.bids || []).reduce((n, b) => n + (b.documents || []).filter((x) => !x.superseded).length, 0) + (d.portfolios || []).reduce((n, p) => n + (p.documents || []).filter((x) => !x.superseded).length, 0)} /></div>
          {(d.portfolios || []).map((p) => <PortfolioCard key={p.portfolio_id} p={p} token={token} primary={primary} preview={preview} />)}
          {d.bids.length === 0 && !(d.portfolios || []).length ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="portal-empty">Nothing new yet. Documents appear here as soon as {company} sends them.</div>
          ) : d.bids.map((bid) => <BidCard key={bid.bid_id} b={bid} token={token} primary={primary} preview={preview} viewer={viewer} />)}
          </>)}
          <div className="mt-6 text-center text-[11px] text-slate-400" data-testid="portal-footer">
            {company}{pr?.name ? ` · ${pr.name}` : ""}{pr?.email ? ` · ${pr.email}` : ""}{pr?.phone ? ` · ${pr.phone}` : ""}
            {d.expires_at ? ` · This link is valid through ${new Date(d.expires_at).toLocaleDateString()}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
